import { DocumentSchema } from '../types';

export interface AttestationLockLink {
    attestationId: string;
    attestationSectionId: string;
    attestationSectionTitle: string;
}

export interface SignedSectionLink {
    sectionId: string;
    sectionTitle: string;
}

export interface AttestationLinkMap {
    /** Sections containing fields that attestations lock → the attestations. */
    lockedBy: Record<string, AttestationLockLink[]>;
    /**
     * Sections containing attestation nodes → the sections those
     * attestations lock, plus the attestation ids living in the section.
     */
    signs: Record<
        string,
        { targets: SignedSectionLink[]; attestationIds: string[] }
    >;
}

const mentionsIdentifier = (expression: string, identifier: string): boolean =>
    new RegExp(`\\b${identifier}\\b`).test(expression);

/**
 * Derive which attestations lock which sections purely from the schema:
 * composite attestation nodes (action checkbox with an `attestation` config)
 * lock every field whose disableWhen expression references their id. No
 * schema changes required — the relationship is already encoded there.
 */
export const collectAttestationLinks = (
    schema?: DocumentSchema | null
): AttestationLinkMap => {
    const lockedBy: AttestationLinkMap['lockedBy'] = {};
    const signs: AttestationLinkMap['signs'] = {};

    const sections = Array.isArray(schema?.sections) ? schema!.sections : [];

    interface AttestationSource {
        attestationId: string;
        sectionId: string;
        sectionTitle: string;
    }
    const attestations: AttestationSource[] = [];

    const collectAttestations = (nodes: any[], section: any) => {
        for (const node of nodes || []) {
            if (
                node?.kind === 'action' &&
                node.actionType === 'checkbox' &&
                node.attestation &&
                typeof node.id === 'string'
            ) {
                attestations.push({
                    attestationId: node.id,
                    sectionId: section.id,
                    sectionTitle: section.title || section.id,
                });
            }
            if (Array.isArray(node?.fields)) {
                collectAttestations(node.fields, section);
            }
        }
    };
    for (const section of sections) {
        collectAttestations(section.fields || [], section);
    }
    if (attestations.length === 0) {
        return { lockedBy, signs };
    }

    const collectLockedFields = (nodes: any[], section: any) => {
        for (const node of nodes || []) {
            const disableWhen =
                typeof node?.disableWhen === 'string' ? node.disableWhen : '';
            if (disableWhen && !(node.kind === 'action' && node.attestation)) {
                for (const attestation of attestations) {
                    if (
                        attestation.sectionId !== section.id &&
                        mentionsIdentifier(
                            disableWhen,
                            attestation.attestationId
                        )
                    ) {
                        const links = (lockedBy[section.id] ||= []);
                        if (
                            !links.some(
                                (link) =>
                                    link.attestationId ===
                                    attestation.attestationId
                            )
                        ) {
                            links.push({
                                attestationId: attestation.attestationId,
                                attestationSectionId: attestation.sectionId,
                                attestationSectionTitle:
                                    attestation.sectionTitle,
                            });
                        }
                        const entry = (signs[attestation.sectionId] ||= {
                            targets: [],
                            attestationIds: [],
                        });
                        if (
                            !entry.targets.some(
                                (target) => target.sectionId === section.id
                            )
                        ) {
                            entry.targets.push({
                                sectionId: section.id,
                                sectionTitle: section.title || section.id,
                            });
                        }
                        if (
                            !entry.attestationIds.includes(
                                attestation.attestationId
                            )
                        ) {
                            entry.attestationIds.push(
                                attestation.attestationId
                            );
                        }
                    }
                }
            }
            if (Array.isArray(node?.fields)) {
                collectLockedFields(node.fields, section);
            }
        }
    };
    for (const section of sections) {
        collectLockedFields(section.fields || [], section);
    }

    return { lockedBy, signs };
};
